
import React, { useRef, useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ArrowDown } from "lucide-react";
import ChatMessage from "../ChatMessage";
import { CortexInitAnimation } from "../CortexInitAnimation";
import { ExtendedMessage } from "../../types/chat";
import { agentBranding } from "../../config/agentBranding";

interface ChatMessageListProps {
  messages: ExtendedMessage[];
  isError: boolean;
  onReplayMessage?: (messageId: string) => void;
  /** Called when user clicks a suggested follow-up; receives the question text (e.g. to send as next message). */
  onFollowUpClick?: (text: string) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
  isInitializing?: boolean;
}

export function ChatMessageList({
  messages,
  isError,
  onReplayMessage,
  onFollowUpClick,
  containerRef,
  isInitializing
}: ChatMessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const internalContainerRef = useRef<HTMLDivElement>(null);
  // Use the passed ref if available, otherwise internal
  const scrollContainerRef = (containerRef ?? internalContainerRef) as React.RefObject<HTMLDivElement>;

  const [showScrollButton, setShowScrollButton] = useState(false);

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  }, [scrollContainerRef]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (messages.length > 0) {
       // Only scroll if we were already near bottom or it's a new user message?
       // Simple behavior: just scroll to bottom for now as per original.
       scrollToBottom();
    }
  }, [messages.length, scrollToBottom]);

  // Handle scroll visibility
  const handleScroll = () => {
    if (!scrollContainerRef.current) {return;}
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShowScrollButton(!isAtBottom);
  };

  return (
    <div className="flex-1 min-h-0 relative flex flex-col overflow-hidden" data-testid="cortex-message-list">
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        data-testid="cortex-message-scroll"
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-4 pb-4 custom-scrollbar"
      >
        {isInitializing && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-background/70 backdrop-blur-sm">
            <CortexInitAnimation
              isVisible={true}
              isLoading={true}
              loadingSteps={agentBranding.loadingSteps}
              className="bg-transparent"
            />
          </div>
        )}
        {messages.map((message, index) => (
          <ChatMessage
            key={message.id}
            message={message}
            isError={isError && message.role === "assistant" && index === messages.length - 1}
            isLatest={index === messages.length - 1}
            onReplay={onReplayMessage ? (msg) => onReplayMessage(msg.id) : undefined}
            onFollowUpClick={onFollowUpClick}
          />
        ))}
        <div ref={messagesEndRef} data-chat-end />
      </div>
      
      {/* Scroll to bottom button */}
      {showScrollButton && (
        <div className="absolute bottom-4 right-4 z-30">
          <Button
            size="sm"
            onClick={scrollToBottom}
            aria-label="Scroll to bottom"
            className="rounded-full shadow-md bg-primary text-primary-foreground hover:brightness-110 gap-2 px-3"
          >
            <ArrowDown className="h-4 w-4" />
            <span className="hidden sm:inline">Jump to latest</span>
          </Button>
        </div>
      )}
    </div>
  );
}
